import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DndContext, closestCenter, DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  Color,
  PieceType,
  Square,
  isFrontRank,
  isPromotionDrop,
  canDropPiece,
  isHighValueFrontRankDrop,
  squareToCell,
  FILES,
  RANKS,
  BoardCell,
  useGameStore,
  useOwnPieces,
  useOpponentSquares,
  useGraveyard,
  useIsMyTurn,
  useIsSetupPhase,
  useLastMove,
  useTimerRemaining,
  useEndgameResult,
  useInCheck,
} from '@fog-chess/core';
import { Piece, MoveArrow, DroppableSquare as SquareComponent, Graveyard, EndgameModal, MoveGhost } from './index';
import type { MoveGhostMode } from './MoveGhost';

const SQUARE_SIZE = 56;
const BOARD_SIZE = SQUARE_SIZE * 8;

/** Promotion piece options */
const PROMOTION_PIECES: PieceType[] = ['Q', 'R', 'B', 'N'];

/** Board callback props — injected by GameScreen (smart container) */
export interface BoardCallbacks {
  /** Called when a valid move is made during gameplay */
  onMove: (from: Square, to: Square) => void;
  /** Called when pawn promotion is resolved */
  onPromote: (from: Square, to: Square, promotion: PieceType) => void;
  /** Called when setup is submitted */
  onSubmitSetup: (placements: { square: Square; type: PieceType }[]) => void;
  /** Called when player resigns */
  onResign: () => void;
}

/** Board props */
export interface BoardProps {
  callbacks: BoardCallbacks;
  /** Optional override for viewer color (defaults to store) */
  viewerColor?: Color | null;
  /** Optional match ID for context */
  matchId?: string | null;
  /** Whether a move is currently being submitted (non-optimistic lock) */
  isSubmittingMove?: boolean;
}

/**
 * Board — Main orchestrator component for the Fog Chess board.
 *
 * Renders:
 * - 8x8 grid of squares with pieces and fog overlays
 * - DndContext for drag-and-drop (setup + gameplay)
 * - Graveyard panel (isolated)
 * - Last move arrow
 * - Promotion dialog
 * - Front rank warning banner
 * - Setup phase submit button
 *
 * ARCHITECTURE:
 * - Pure presentational component: receives callbacks via props
 * - No direct socket access — GameScreen wires callbacks to socket
 * - Container/Visual pattern for dnd-kit (SquareVisual, PieceVisual are memoized)
 * - Graveyard isolated to prevent re-render cascades
 */
export function Board({ callbacks, viewerColor: propViewerColor, matchId, isSubmittingMove: propIsSubmittingMove }: BoardProps) {
  // Game state selectors (granular to prevent re-renders)
  const status = useGameStore((s) => s.status);
  const storeViewerColor = useGameStore((s) => s.viewerColor);
  const isMyTurn = useIsMyTurn();
  const isSetupPhase = useIsSetupPhase();
  const ownPieces = useOwnPieces();
  const opponentSquares = useOpponentSquares();
  const graveyard = useGraveyard();
  const lastMove = useLastMove();
  const timerRemaining = useTimerRemaining();
  const setupAccepted = useGameStore((s) => s.setupAccepted);
  const isSubmittingMove = propIsSubmittingMove ?? useGameStore((s) => s.isSubmittingMove);
  const endgameResult = useEndgameResult();
  
  const viewerColor = propViewerColor ?? storeViewerColor;

  // Check pulse — the server only sends check:warning to the threatened
  // player, so inCheck===true implies the VIEWER's own king is in check.
  // The opponent's (fogged) king is never pulsable from this client's view.
  const inCheck = useInCheck();
  const ownKingSquare = useMemo(
    () => ownPieces.find((p) => p.type === 'K')?.square ?? null,
    [ownPieces],
  );

  // Capture animation trigger (incremented when piece:captured received)
  const [captureTrigger, setCaptureTrigger] = useState(0);
  
  // Track last capture for animation trigger
  const lastCapture = useGameStore((s) => s.lastCapture);
  const prevCaptureRef = useRef<typeof lastCapture>(null);
  
  // Increment trigger when new capture arrives
  useEffect(() => {
    if (lastCapture && lastCapture !== prevCaptureRef.current) {
      setCaptureTrigger(prev => prev + 1);
      prevCaptureRef.current = lastCapture;
    }
  }, [lastCapture]);

  // ---------------------------------------------------------------------------
  // Move ghost — tactile slide on move:applied (Task 4.1).
  // The server's VisibleMove payload is geometry-only (no piece identity), so:
  //  - viewer's own move (mover_color === viewerColor): animate the real piece
  //    SVG using the type known from ownPieces (resolved at the destination
  //    after the server pushes the updated fogView).
  //  - opponent's move: animate a non-identifying fog shroud (no identity leak).
  // The overlay is non-interactive and tears itself down via MoveGhost.onComplete.
  // ---------------------------------------------------------------------------
  const [activeMoveGhost, setActiveMoveGhost] = useState<{
    key: number;
    fromLeft: number;
    fromTop: number;
    toLeft: number;
    toTop: number;
    mode: MoveGhostMode;
    type?: PieceType;
    color?: Color;
    isCapture: boolean;
  } | null>(null);
  const moveGhostKeyRef = useRef(0);

  // Board orientation
  const flipped = viewerColor === 'black';

  useEffect(() => {
    // Only animate real moves during gameplay (setup has no move:applied).
    if (!lastMove || status !== 'IN_PROGRESS' || !viewerColor) {
      return;
    }

    // Resolve pixel rects using the same orientation math as last-move overlays.
    const fromCell = squareToCell(lastMove.from_square);
    const toCell = squareToCell(lastMove.to_square);
    const fromCol = flipped ? 7 - fromCell.col : fromCell.col;
    const fromRow = flipped ? 7 - fromCell.row : fromCell.row;
    const toCol = flipped ? 7 - toCell.col : toCell.col;
    const toRow = flipped ? 7 - toCell.row : toCell.row;

    const fromLeft = fromCol * SQUARE_SIZE;
    const fromTop = fromRow * SQUARE_SIZE;
    const toLeft = toCol * SQUARE_SIZE;
    const toTop = toRow * SQUARE_SIZE;

    const isOwnMove = lastMove.mover_color === viewerColor;

    if (isOwnMove) {
      // The viewer's own piece: type is known. Prefer the destination (post-move)
      // lookup; fall back to the source in case the fogView update lags by a tick.
      const settledAtTo = ownPieces.find((p) => p.square === lastMove.to_square);
      const settledAtFrom = ownPieces.find((p) => p.square === lastMove.from_square);
      const movedPiece = settledAtTo ?? settledAtFrom;
      if (!movedPiece) {
        // Piece not resolvable this tick — skip the ghost rather than leak.
        return;
      }
      setActiveMoveGhost({
        key: ++moveGhostKeyRef.current,
        fromLeft,
        fromTop,
        toLeft,
        toTop,
        mode: 'piece',
        type: movedPiece.type,
        color: viewerColor,
        isCapture: lastMove.is_capture,
      });
    } else {
      // Opponent move: piece identity is hidden — animate a fog shroud only.
      setActiveMoveGhost({
        key: ++moveGhostKeyRef.current,
        fromLeft,
        fromTop,
        toLeft,
        toTop,
        mode: 'fog',
        isCapture: lastMove.is_capture,
      });
    }
    // ownPieces intentionally omitted: we re-derive the ghost solely from the
    // lastMove identity + viewerColor + orientation; including ownPieces would
    // re-fire the effect on every fogView change and restart the animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMove, status, viewerColor, flipped]);

  const handleMoveGhostComplete = useCallback(() => {
    setActiveMoveGhost(null);
  }, []);

  // Local UI state
  const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<{
    from: Square;
    to: Square;
    color: Color;
  } | null>(null);
  const [frontRankWarning, setFrontRankWarning] = useState<{
    square: Square;
    pieceType: PieceType;
    color: Color;
  } | null>(null);
  const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);

  // Refs for drag state
  const activeDragRef = useRef<{
    id: string;
    piece: { type: PieceType; color: Color };
    sourceSquare: Square;
  } | null>(null);

  // Build occupancy map
  const occupiedSquares = useMemo(() => {
    const set = new Set<Square>();
    for (const p of ownPieces) {
      set.add(p.square);
    }
    for (const s of opponentSquares) {
      set.add(s.square);
    }
    return set;
  }, [ownPieces, opponentSquares]);

  // Generate board cells with correct orientation
  const boardCells = useMemo((): BoardCell[] => {
    const cells: BoardCell[] = [];
    const ranks = flipped ? [...RANKS].reverse() : RANKS;
    const files = flipped ? [...FILES].reverse() : FILES;

    for (const rank of ranks) {
      for (const file of files) {
        cells.push({ row: RANKS.indexOf(rank), col: FILES.indexOf(file), square: `${file}${rank}` as Square });
      }
    }
    return cells;
  }, [flipped]);

  // Check square occupancy
  const getSquareOccupancy = useCallback(
    (square: Square): { piece?: { type: PieceType; color: Color }; isOwn: boolean } | null => {
      const own = ownPieces.find((p) => p.square === square);
      if (own) return { piece: { type: own.type, color: viewerColor! }, isOwn: true };
      
      const opp = opponentSquares.find((s) => s.square === square);
      if (opp) return { isOwn: false };
      
      return null;
    },
    [ownPieces, opponentSquares, viewerColor],
  );

  // Check if square is in fog
  const isFogSquare = useCallback(
    (square: Square): boolean => {
      return opponentSquares.some((s) => s.square === square);
    },
    [opponentSquares],
  );

  // Square click handler (selection)
  const handleSquareClick = useCallback(
    (square: Square) => {
      const occupancy = getSquareOccupancy(square);

      if (selectedSquare === square) {
        setSelectedSquare(null);
        return;
      }

      if (isSetupPhase && occupancy?.isOwn) {
        setSelectedSquare(square);
      } else if (!isSetupPhase && isMyTurn && occupancy?.isOwn) {
        setSelectedSquare(square);
      } else {
        setSelectedSquare(null);
      }
    },
    [selectedSquare, getSquareOccupancy, isSetupPhase, isMyTurn],
  );

  // Drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    // NON-OPTIMISTIC: Prevent drag during move submission
    if (isSubmittingMove) {
      return;
    }
    
    // Only allow drag on own turn during gameplay
    if (!isSetupPhase && !isMyTurn) {
      return;
    }
    
    const square = String(event.active.id).replace('piece-', '') as Square;
    const occupancy = getSquareOccupancy(square);
    if (occupancy && occupancy.piece) {
      activeDragRef.current = {
        id: String(event.active.id),
        piece: { type: occupancy.piece.type, color: viewerColor! },
        sourceSquare: square,
      };
    }
  }, [getSquareOccupancy, viewerColor, isSetupPhase, isMyTurn, isSubmittingMove]);

  // Drag end — validate and commit or intercept
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      // NON-OPTIMISTIC: Prevent move processing during submission
      if (isSubmittingMove) {
        activeDragRef.current = null;
        return;
      }
      
      const { active, over } = event;

      if (!over) {
        activeDragRef.current = null;
        return;
      }

      const sourceSquare = String(active.id).replace('piece-', '') as Square;
      const targetSquare = String(over.id) as Square;

      if (sourceSquare === targetSquare) {
        activeDragRef.current = null;
        return;
      }

      const dragData = activeDragRef.current;
      activeDragRef.current = null;

      if (!dragData) return;

      const { piece } = dragData;

      // Pawn promotion interception
      if (isPromotionDrop(sourceSquare, targetSquare, piece.type, piece.color)) {
        setPendingPromotion({ from: sourceSquare, to: targetSquare, color: piece.color });
        setPromotionDialogOpen(true);
        return;
      }

      // Front rank warning (Q/R on front rank during setup)
      if (isSetupPhase && isHighValueFrontRankDrop(targetSquare, piece.type, piece.color, 'setup')) {
        setFrontRankWarning({ square: targetSquare, pieceType: piece.type, color: piece.color });
      } else {
        setFrontRankWarning(null);
      }

      // Validate drop during setup
      if (isSetupPhase) {
        const placements = ownPieces.map((p) => ({ square: p.square, type: p.type }));
        const dropResult = canDropPiece(targetSquare, piece.type, piece.color, 'setup', placements);

        if (!dropResult.value) {
          // Invalid drop — reject (dnd-kit snaps back)
          return;
        }
      }

      // Valid move — call parent callback
      callbacks.onMove(sourceSquare, targetSquare);
    },
    [isSetupPhase, ownPieces, viewerColor, callbacks, isSubmittingMove],
  );

  // Drag over (visual feedback handled by CSS)
  const handleDragOver = useCallback(() => {}, []);

  // Drag cancel
  const handleDragCancel = useCallback(() => {
    activeDragRef.current = null;
  }, []);

  // Submit setup
  const handleSubmitSetup = useCallback(() => {
    if (!viewerColor || !matchId) return;

    const placements = ownPieces.map((p) => ({ square: p.square, type: p.type }));
    callbacks.onSubmitSetup(placements);
  }, [viewerColor, matchId, ownPieces, callbacks]);

  // Resolve promotion
  const handlePromotionResolve = useCallback(
    (promotionPiece: PieceType) => {
      if (!pendingPromotion) return;
      callbacks.onPromote(pendingPromotion.from, pendingPromotion.to, promotionPiece);
      setPendingPromotion(null);
      setPromotionDialogOpen(false);
    },
    [pendingPromotion, callbacks],
  );

  // Pre-compute last move highlight positions (clean extraction)
  const lastMoveFromStyle = useMemo(() => {
    if (!lastMove) return null;
    const cell = squareToCell(lastMove.from_square);
    const col = flipped ? 7 - cell.col : cell.col;
    const row = flipped ? 7 - cell.row : cell.row;
    return {
      left: `${col * SQUARE_SIZE}px`,
      top: `${row * SQUARE_SIZE}px`,
      width: SQUARE_SIZE,
      height: SQUARE_SIZE,
      pointerEvents: 'none' as const,
    };
  }, [lastMove, flipped]);

  const lastMoveToStyle = useMemo(() => {
    if (!lastMove) return null;
    const cell = squareToCell(lastMove.to_square);
    const col = flipped ? 7 - cell.col : cell.col;
    const row = flipped ? 7 - cell.row : cell.row;
    return {
      left: `${col * SQUARE_SIZE}px`,
      top: `${row * SQUARE_SIZE}px`,
      width: SQUARE_SIZE,
      height: SQUARE_SIZE,
      pointerEvents: 'none' as const,
    };
  }, [lastMove, flipped]);

  // Check pulse — rect of the viewer's own king when inCheck is true. Board-level
  // overlay (sibling to last-move highlights / MoveArrow); keeps SquareVisual's
  // memo comparator untouched (regression guard).
  const checkPulseStyle = useMemo(() => {
    if (!inCheck || !ownKingSquare) return null;
    const cell = squareToCell(ownKingSquare);
    const col = flipped ? 7 - cell.col : cell.col;
    const row = flipped ? 7 - cell.row : cell.row;
    return {
      left: `${col * SQUARE_SIZE}px`,
      top: `${row * SQUARE_SIZE}px`,
      width: SQUARE_SIZE,
      height: SQUARE_SIZE,
      pointerEvents: 'none' as const,
    };
  }, [inCheck, ownKingSquare, flipped]);

  // Render rank labels (a-h)
  const renderRankLabels = useCallback(() => {
    const files = flipped ? [...FILES].reverse() : FILES;
    return (
      <div className="flex h-8 w-full items-center justify-between px-8">
        {files.map((file) => (
          <div key={file} className="w-[56px] text-center text-xs font-mono text-muted-foreground">
            {file}
          </div>
        ))}
      </div>
    );
  }, [flipped]);

  // Render file labels (1-8)
  const renderFileLabels = useCallback(() => {
    const ranks = flipped ? [...RANKS].reverse() : RANKS;
    return (
      <div className="flex h-full flex-col justify-between py-8">
        {ranks.map((rank) => (
          <div key={rank} className="h-[56px] w-8 flex items-center justify-center text-xs font-mono text-muted-foreground">
            {rank}
          </div>
        ))}
      </div>
    );
  }, [flipped]);

  // Render a single square
  const renderSquare = useCallback(
    (cell: BoardCell) => {
      const { square } = cell;
      const isLight = (cell.row + cell.col) % 2 === 1;
      const occupancy = getSquareOccupancy(square);
      const fog = isFogSquare(square);

      // King shield forbidden (King on front rank during setup)
      const isKingShieldForbidden =
        isSetupPhase && occupancy?.piece?.type === 'K' && isFrontRank(square, viewerColor!);

      // Front rank warning (Q/R on front rank during setup)
      const isFrontRankWarned =
        isSetupPhase &&
        frontRankWarning?.square === square &&
        (frontRankWarning.pieceType === 'Q' || frontRankWarning.pieceType === 'R');

      // Last move highlights
      const isLastMoveFrom = lastMove?.from_square === square;
      const isLastMoveTo = lastMove?.to_square === square;

      // Selected highlight
      const isSelected = selectedSquare === square;

      return (
        <SquareComponent
          key={square}
          square={square}
          isLight={isLight}
          isSelected={isSelected}
          isLastMoveFrom={isLastMoveFrom}
          isLastMoveTo={isLastMoveTo}
          isKingShieldForbidden={isKingShieldForbidden}
          isFrontRankWarning={isFrontRankWarned}
          piece={occupancy?.piece || null}
          fogState={fog ? 'hidden' : occupancy ? 'visible' : 'none'}
          isSetupPhase={isSetupPhase}
          isGameplayPhase={!isSetupPhase}
          isMyTurn={isMyTurn}
          viewerColor={viewerColor}
          occupiedSquares={occupiedSquares}
          activeDragPiece={activeDragRef.current ? { type: activeDragRef.current.piece.type, color: activeDragRef.current.piece.color } : null}
          onSquareClick={() => handleSquareClick(square)}
        />
      );
    },
    [
      getSquareOccupancy,
      isFogSquare,
      isSetupPhase,
      isMyTurn,
      viewerColor,
      flipped,
      frontRankWarning,
      lastMove,
      selectedSquare,
      handleSquareClick,
      occupiedSquares,
    ],
  );

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Match header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-[520px] flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            ♟
          </div>
          <div>
            <h1 className="text-xl font-bold">Match {matchId?.slice(0, 8)}</h1>
            <p className="text-sm text-muted-foreground">
              {status === 'SETUP' && 'Setting up pieces...'}
              {status === 'SETUP_LOCKED' && 'Setup locked, waiting for opponent...'}
              {status === 'IN_PROGRESS' && (isMyTurn ? 'Your turn' : "Opponent's turn")}
              {status === 'FINISHED' && 'Game over'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Time</p>
            <p className="font-mono text-lg font-bold tabular-nums">
              {Math.floor(timerRemaining / 60)}:{String(timerRemaining % 60).padStart(2, '0')}
            </p>
          </div>
          
          {/* Move submission lock indicator */}
          <AnimatePresence mode="wait">
            {isSubmittingMove && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.15 }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50/80 dark:bg-blue-900/30 rounded-full"
              >
                <div className="flex h-4 w-4 animate-spin">
                  <svg className="h-full w-full text-current" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                  </svg>
                </div>
                <span>Submitting move…</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Front rank warning banner — Tailwind utilities (no custom CSS var) */}
      <AnimatePresence mode="wait">
        {frontRankWarning && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-[520px]"
          >
            <div className="flex items-start gap-3 p-4 border border-amber-500/50 bg-amber-50/50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 rounded-lg" role="alert">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-semibold">⚠ Blind Sniping Risk</p>
                <p className="text-sm mt-1">
                  Placing high-value pieces on the front rank exposes them to Blind Sniping on Move 1. You assume the risk!
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Board */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="relative"
      >
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragCancel={handleDragCancel}
        >
          <div className="flex">
            {/* File labels (left) */}
            {renderFileLabels()}

            {/* Board grid */}
            <div
              className="relative"
              style={{ width: BOARD_SIZE, height: BOARD_SIZE }}
              role="grid"
              aria-label="Chess board"
            >
              {/* Base squares */}
              <div
                className="absolute inset-0 grid grid-cols-8 grid-rows-8"
                style={{ width: '100%', height: '100%' }}
              >
                {boardCells.map((cell) => (
                  <div
                    key={cell.square}
                    className={cn(
                      'relative',
                      (cell.row + cell.col) % 2 === 1
                        ? 'bg-[hsl(var(--board-light))]'
                        : 'bg-[hsl(var(--board-dark))]',
                    )}
                    style={{ width: SQUARE_SIZE, height: SQUARE_SIZE }}
                  />
                ))}
              </div>

              {/* Last move highlights */}
              {lastMoveFromStyle && (
                <div className="absolute bg-[hsl(var(--board-last-move))/0.5]" style={lastMoveFromStyle} />
              )}
              {lastMoveToStyle && (
                <div className="absolute bg-[hsl(var(--board-last-move))/0.5]" style={lastMoveToStyle} />
              )}

              {/* Check pulse — continuous red glow on the viewer's own king (Task 4.1) */}
              {checkPulseStyle && (
                <motion.div
                  aria-hidden="true"
                  style={checkPulseStyle}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0.35, 0.85, 0.35] }}
                  transition={{
                    duration: 1.4,
                    ease: 'easeInOut',
                    repeat: Infinity,
                    repeatType: 'loop',
                  }}
                  className="absolute rounded-md ring-2 ring-inset ring-[hsl(var(--board-check))] shadow-[0_0_18px_2px_hsl(var(--board-check)/0.45)]"
                />
              )}

              {/* Move arrow */}
              {lastMove && (
                <MoveArrow
                  from={lastMove.from_square}
                  to={lastMove.to_square}
                  color={lastMove.mover_color}
                  squareSize={SQUARE_SIZE}
                  flipped={flipped}
                />
              )}

              {/* Move ghost — tactile slide on move:applied (Task 4.1) */}
              {activeMoveGhost && (
                <MoveGhost
                  ghostKey={activeMoveGhost.key}
                  fromLeft={activeMoveGhost.fromLeft}
                  fromTop={activeMoveGhost.fromTop}
                  toLeft={activeMoveGhost.toLeft}
                  toTop={activeMoveGhost.toTop}
                  squareSize={SQUARE_SIZE}
                  isCapture={activeMoveGhost.isCapture}
                  mode={activeMoveGhost.mode}
                  {...(activeMoveGhost.type && { type: activeMoveGhost.type })}
                  {...(activeMoveGhost.color && { color: activeMoveGhost.color })}
                  onComplete={handleMoveGhostComplete}
                />
              )}

              {/* Pieces and fog overlays */}
              {boardCells.map((cell) => (
                <div
                  key={cell.square}
                  className="absolute"
                  style={{
                    left: `${(flipped ? 7 - cell.col : cell.col) * SQUARE_SIZE}px`,
                    top: `${(flipped ? 7 - cell.row : cell.row) * SQUARE_SIZE}px`,
                    width: SQUARE_SIZE,
                    height: SQUARE_SIZE,
                  }}
                >
                  {renderSquare(cell)}
                </div>
              ))}
            </div>

            {/* File labels (right) */}
            {renderFileLabels()}
          </div>
        </DndContext>
      </motion.div>

      {/* Rank labels (bottom) */}
      <div className="w-full max-w-[520px]">{renderRankLabels()}</div>

      {/* Graveyard & Controls */}
      <div className="w-full max-w-[520px] flex items-start justify-between gap-4">
        {/* Graveyard */}
        <Graveyard 
          graveyard={graveyard} 
          viewerColor={viewerColor} 
          compact 
          captureTrigger={captureTrigger}
        />
        <div className="flex flex-col items-end gap-2">
          {isSetupPhase && !setupAccepted && (
            <Button onClick={handleSubmitSetup} size="lg" className="w-full max-w-xs">
              Submit Setup
            </Button>
          )}
          {!isSetupPhase && (
            <Button
              variant="destructive"
              onClick={callbacks.onResign}
              disabled={!isMyTurn}
              className="w-full max-w-xs"
            >
              Resign
            </Button>
          )}
        </div>
      </div>

      {/* Promotion Dialog */}
      <Dialog open={promotionDialogOpen} onOpenChange={setPromotionDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Promote Pawn</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-3 py-4">
            {PROMOTION_PIECES.map((piece) => (
              <Button
                key={piece}
                variant="outline"
                size="lg"
                className="aspect-square p-0"
                onClick={() => handlePromotionResolve(piece)}
              >
                <Piece type={piece} color={pendingPromotion?.color || 'white'} size={48} />
              </Button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPromotionDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Endgame Modal */}
      <EndgameModal
        result={endgameResult}
        viewerColor={viewerColor}
        isOpen={!!endgameResult}
        onClose={() => useGameStore.getState().setEndgameResult(null)}
      />
    </div>
  );
}

// ============================================================================
// Type exports (already exported inline)
// ============================================================================

export default Board;